import type { FiQuestionnaireSystemTaskData, Group, IANATimezones, User } from '@gcv/shared';
import type { VerboseQuestionnaireMetadata } from '@gcv/shared';
import { TaskStatus, TaskUser } from '@gcv/shared';

import { lookupQuestionnaireFrequencyLabel } from 'domain/consts/due-diligence';
import { DateTimeHelpers } from 'util/dateTime.util';
import type { DecoratedTask, TaskRow } from '../../tasks/tasks.model';
import {
  getAssigneeName,
  getGroupedAccountNames,
  getGroupedAssigneeName,
  getGroupedStatus
} from '../../tasks/util';
import { getTaskRowType } from './get-task-row-type';

export interface TaskRowContext {
  bankGroups: Group[];
  timezone: IANATimezones;
  questionnaires: { [templateId: string]: VerboseQuestionnaireMetadata };
}

const getFrequency = (task: DecoratedTask['tasks'][number]): string => {
  const questionnaire = (task.data as FiQuestionnaireSystemTaskData)?.questionnaire_fi;

  return questionnaire?.reoccurring ? lookupQuestionnaireFrequencyLabel(questionnaire.frequency) : 'One-Time';
};

const getCreatedByUser = (task: DecoratedTask['tasks'][number]): string => {
  if (task.created_by_user === TaskUser.AUTO_GENERATED_USER) return 'Auto Generated';
  if (task.created_by_user?.firstName && task.created_by_user.lastName) {
    return `${task.created_by_user.firstName} ${task.created_by_user.lastName}`;
  }
  return '';
};

/** Bank-owned tasks carry the account separately; a CRB's own task is its account. */
const getAccountName = (task: DecoratedTask['tasks'][number]): string =>
  task.assigned_org.orgType === 'bank' ? task.account?.name ?? '--' : task.assigned_org?.name;

export const getTaskRow = (decoratedTask: DecoratedTask, context: TaskRowContext): TaskRow => {
  const { bankGroups, timezone, questionnaires } = context;
  const tasks = decoratedTask.tasks;
  const task = tasks[0];

  const groupedAssignee = decoratedTask.isGrouped ? getGroupedAssigneeName(bankGroups, tasks) : null;
  const assignee =
    groupedAssignee?.label ??
    getAssigneeName(bankGroups, task.assigned_users, task.assigned_group_ids, task.assigned_groups);
  const assigneeNames = groupedAssignee ? groupedAssignee.uniqueNames : [assignee];

  const templateId = (task.data as FiQuestionnaireSystemTaskData)?.questionnaire_fi?.template_id;
  const shared = {
    assignee,
    assigneeNames,
    associatedWith: task.associated_with ?? '--',
    created: DateTimeHelpers.formatISOToTableDateString(task.date_created, timezone),
    createdBy: getCreatedByUser(task),
    createdTimestamp: new Date(task.date_created).getTime(),
    completedTimestamp: task.completed_on ? new Date(task.completed_on).getTime() : undefined,
    frequency: getFrequency(task),
    groups: task.assigned_group_ids ?? [],
    is_managed_template: questionnaires[templateId]?.is_managed_template,
    sentOn: DateTimeHelpers.formatISOToTableDateString(task.date_created, timezone),
    taskName: task.title ?? '--',
    type: getTaskRowType(task.internal_type)
  };

  if (decoratedTask.isGrouped) {
    const completedDates = [...new Set(tasks.map((t) => t.completed_on))];
    const completedUsers = [
      ...new Set(
        tasks.map((t) => {
          const user = t.completed_by_user as User;
          return user ? user.firstName + ' ' + user.lastName : '--';
        })
      )
    ];
    const completedBy = completedUsers.length > 1 ? 'Multiple Users' : completedUsers[0];
    const openTasks = tasks.filter((t) => t.status === TaskStatus.OPEN || t.status === TaskStatus.PROCESSING);

    return {
      ...shared,
      account: tasks.length > 1 ? 'Multiple Accounts' : getAccountName(task),
      accountNames: getGroupedAccountNames(tasks),
      completed:
        DateTimeHelpers.formatISOToTableDateString(completedDates.sort().reverse()[0] ?? '', timezone) ??
        '--',
      completedBy: (completedBy === TaskUser.AUTO_GENERATED_USER ? 'Autocompleted' : completedBy) ?? '--',
      dueDate: openTasks[0]?.process_date ?? task.process_date,
      id: decoratedTask.groupKey,
      status: getGroupedStatus(tasks)
    };
  }

  const account = getAccountName(task);
  const completedUser =
    task.completed_by_user &&
    (task.completed_by_user as User).firstName + ' ' + (task.completed_by_user as User).lastName;

  return {
    ...shared,
    account: account ? account : '--',
    accountNames: [account],
    completed: DateTimeHelpers.formatISOToTableDateString(task.completed_on ?? '', timezone),
    completedBy: completedUser ? completedUser : '--',
    dueDate: task.process_date,
    id: task.id,
    status: task.status ?? '--'
  };
};
